import { FC, memo, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate } from 'react-router-dom';
import { useUserContext } from '../UsersPage/hook/useUserContext';
import { UserProvider } from '../UsersPage/context/userContext';
import {
    FormTextField,
    FormSelect,
    FormPhoneInput,
    FormSection,
    FormActions,
} from '../../common/Form';
import { Box, Container, Alert } from '@mui/material';
import {
    emailSchema,
    phoneSchema,
    requiredStringSchema,
} from '../../utils/validationSchemas';
import UserRole from '../../security/type/enum/UserRole';


const userCreateSchema = z.object({
    phoneNumber: phoneSchema,
    firstName: requiredStringSchema('First name').min(2, 'First name must be at least 2 characters').max(20, 'First name must be at most 20 characters'),
    lastName: requiredStringSchema('Last name').min(2, 'Last name must be at least 2 characters').max(20, 'Last name must be at most 20 characters'),
    mail: emailSchema.optional().or(z.literal('')),
    password: z.string().optional(),
    role: z.nativeEnum(UserRole, {
        errorMap: () => ({ message: 'Role is required' }),
    }),
});

type UserCreateFormData = z.infer<typeof userCreateSchema>;

interface IUserCreatePage {}

const UserCreatePage: FC = () => {
    const { createNewUser } = useUserContext();
    const navigate = useNavigate();
    const [submitSuccess, setSubmitSuccess] = useState(false);

    // Initialize form with validation
    const methods = useForm<UserCreateFormData>({
        resolver: zodResolver(userCreateSchema),
        defaultValues: {
            phoneNumber: '',
            firstName: '',
            lastName: '',
            mail: '',
            password: '',
            role: UserRole.ROLE_CLIENT,
        },
        mode: 'onBlur',
    });

    const {
        handleSubmit,
        reset,
        formState: { isSubmitting },
    } = methods;
    const roleOptions = [
        { value: UserRole.ROLE_CLIENT, label: 'Client' },
        { value: UserRole.ROLE_OPERATOR, label: 'Operator' },
        { value: UserRole.ROLE_OPERATOR_SUPERVISOR, label: 'Operator Supervisor' },
        { value: UserRole.ROLE_STOREKEEPER, label: 'Storekeeper' },
        { value: UserRole.ROLE_ADMIN, label: 'Admin' },
    ];

    const onSubmit = async (data: UserCreateFormData) => {
        try {
            const userRequestData = {
                id: null,
                role: data.role,
                firstName: data.firstName,
                lastName: data.lastName,
                phoneNumber: data.phoneNumber,
                mail: data.mail && data.mail.trim() ? data.mail.trim() : null,
                password: data.password && data.password.trim() ? data.password.trim() : null,
                isActive: true,
            };

            console.log('Sending user data:', userRequestData);
            await createNewUser(userRequestData as any);

            setSubmitSuccess(true);
            reset();
            setTimeout(() => {
                setSubmitSuccess(false);
                navigate('/users');
            }, 2000);
        } catch (error) {
            console.error('Failed to create user:', error);
        }
    };

    const handleCancel = () => {
        navigate('/main/users');
    };

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <FormProvider {...methods}>
                <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
                    {submitSuccess && (
                        <Alert severity="success" sx={{ mb: 3 }}>
                            User created successfully! Redirecting to users list...
                        </Alert>
                    )}
                    <FormSection
                        title="Create New User"
                        description="Enter user details to create a new account"
                        columns={2}
                        elevation={2}
                    >
                        <FormPhoneInput
                            name="phoneNumber"
                            label="Phone Number"
                            required
                            defaultCountry="MD"
                            showCountrySelect
                            helperText="International phone number with country code"
                        />
                        <FormTextField
                            name="firstName"
                            label="First Name"
                            placeholder="Enter first name"
                            required
                        />
                        <FormTextField
                            name="lastName"
                            label="Last Name"
                            placeholder="Enter last name"
                            required
                        />
                        <FormTextField
                            name="mail"
                            label="Email Address"
                            placeholder="user@example.com"
                            type="email"
                            helperText="Optional"
                        />
                        <FormTextField
                            name="password"
                            label="Password"
                            placeholder="Enter password"
                            type="password"
                            helperText="Optional - if empty, user will need to reset password"
                        />

                        <FormSelect
                            name="role"
                            label="Role"
                            options={roleOptions}
                            required
                            helperText="Select the user's role in the system"
                        />
                    </FormSection>
                    <FormActions
                        onCancel={handleCancel}
                        submitLabel="Create User"
                        cancelLabel="Cancel"
                        isSubmitting={isSubmitting}
                    />
                </Box>
            </FormProvider>
        </Container>
    );
};

const UsersCreateProviderHOC: FC<IUserCreatePage> = memo((props: IUserCreatePage) => {
    return (
        <UserProvider>
            <UserCreatePage {...props} />
        </UserProvider>
    );
});

export default UsersCreateProviderHOC;